/**
 * Homescreen (§12.1, PRD §1; redesign § Homescreen).
 *
 * One centered column: welcome row (bell + gear pinned to the page's top-right corner) → quiet
 * status stack → centered Feed / Add / Scrape buttons with the live queue counter → the **watching
 * callout** (#94) → **New** → Groups → Profiles. The old top bar and status strip are gone: logout lives inside the Settings
 * popup, their remaining content is folded in here. All server state comes from four queries
 * (`/profiles`, `/groups`, `/status`, `/notifications`) kept live by the SSE bridge; this screen
 * holds only dialog and toggle state.
 *
 * The page is wrapped in `PostPreviewProvider` so a viral card in the New strip opens the same
 * `?post=` popup the feed does — the provider only needs the search params, not the view route. A
 * `new_post` card leaves for `/watch?post=` instead, where the same popup opens over that feed.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ProfileRowDTO, StatusResponse } from '@lw/shared';
import { requestRemap } from '../../api/jobs';
import { QK } from '../../api/queryKeys';
import { describeError } from '../../api/client';
import {
  BellIcon,
  ChartIcon,
  EmptyState,
  ErrorState,
  BookmarkFilledIcon,
  EyeIcon,
  FeedIcon,
  GearIcon,
  PlusIcon,
  RefreshIcon,
  Spinner,
  cx,
} from '../../components';
import { pageTitle } from '../../app/appTitle';
import {
  CONTENT_PATH,
  FEED_PATH,
  NOTIFICATIONS_PATH,
  SAVED_PATH,
  WATCH_PATH,
} from '../../app/routes';
import { formatDateTime } from '../../lib/datetime';
import { pluralize } from '../../lib/format';
import { useDocumentTitle } from '../../lib/hooks';
import { JOB_STATUS_LABEL, JOB_TYPE_LABEL, SCRAPER_BANNER_LABEL } from '../../lib/labels';
import { NewViralStrip } from '../notifications/NewViralStrip';
import { useUnreadNotifications } from '../notifications/useNotifications';
import { SettingsModal } from '../settings/SettingsModal';
import { PostPreviewProvider } from '../view/PreviewContext';
import { useAppTimezone, useMediaEnabled, useTimezoneBootstrap } from '../settings/useSettings';
import { AddProfilesDialog } from './AddProfilesDialog';
import { BackfillDialog } from './BackfillDialog';
import { GroupsSection } from './GroupsSection';
import { ProfilesTable, type ArchiveFilter, type GroupFilter } from './ProfilesTable';
import { ScrapeHistoryDialog } from './ScrapeHistoryDialog';
import { ScrapeNowDialog } from './ScrapeNowDialog';
import { WatchCallout } from './WatchCallout';
import { useGroupsQuery, useProfilesQuery, useStartScrape, useStatusQuery } from './useHomeData';
import './home.css';

/**
 * The §11.2 flow, folded into the home column (redesign § Homescreen "Status stack") and fed by
 * `GET /api/status` (+ SSE `status.changed`): per-plugin `auth`/`quota` banners (they stack —
 * per-plugin credentials mean one plugin's rejected key says nothing about another's, §11.1), the
 * "N profiles have issues" filter chip, the remap-recommended chip (§7.5) and live background-job
 * progress. Renders nothing at all when there is nothing to say.
 */
type HomeStatusStackProps = {
  status: StatusResponse | undefined;
  /** Number of distinct profiles counted by the indicator (error + mapper error). */
  issueCount: number;
  issuesFilterOn: boolean;
  onToggleIssuesFilter: () => void;
  /** A banner opens Settings on the Scrapers tab — that is where the fix lives. */
  onOpenSettings: (tab?: 'general' | 'scrapers' | 'watching') => void;
  timezone: string;
};

function HomeStatusStack({
  status,
  issueCount,
  issuesFilterOn,
  onToggleIssuesFilter,
  onOpenSettings,
  timezone,
}: HomeStatusStackProps) {
  const queryClient = useQueryClient();
  const remap = useMutation({
    mutationFn: () => requestRemap({}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QK.status });
      void queryClient.invalidateQueries({ queryKey: QK.jobs });
    },
  });

  if (status === undefined) return null;

  const job = status.activeJob;
  const hasChips = issueCount > 0 || status.remapRecommended || job !== null || remap.isError;
  if (status.scraperBanners.length === 0 && !hasChips) return null;

  return (
    <div className="home-status">
      {status.scraperBanners.map((banner) => (
        <div key={banner.pluginId} className="card home-status__banner" role="alert">
          <span className="status status--error">
            <span className="status__dot" aria-hidden="true" />
          </span>
          <span className="home-status__text">
            <strong>
              {banner.pluginName}: {SCRAPER_BANNER_LABEL[banner.class]}
            </strong>{' '}
            {banner.message}{' '}
            <span className="faint">Since {formatDateTime(banner.since, timezone)}.</span>
          </span>
          <button type="button" className="table-action" onClick={() => onOpenSettings('scrapers')}>
            <span className="table-action__label">Open Settings</span>
          </button>
        </div>
      ))}

      {hasChips ? (
        <div className="home-status__chips">
          {issueCount > 0 ? (
            <button
              type="button"
              className={cx('chip', issuesFilterOn && 'chip--active')}
              onClick={onToggleIssuesFilter}
              aria-pressed={issuesFilterOn}
            >
              <span className="status status--error">
                <span className="status__dot" aria-hidden="true" />
              </span>
              {pluralize(issueCount, 'profile')} {issueCount === 1 ? 'has' : 'have'} issues
              {issuesFilterOn ? ' · showing only these' : ''}
            </button>
          ) : null}

          {status.remapRecommended ? (
            <span className="chip">
              <span className="status status--queued">
                <span className="status__dot" aria-hidden="true" />
              </span>
              Mapper updated — re-map recommended
              <button
                type="button"
                className="btn-ghost btn-ghost--sm"
                onClick={() => remap.mutate()}
                disabled={remap.isPending || job !== null}
              >
                {remap.isPending ? <Spinner size="sm" /> : null}
                Re-map now
              </button>
            </span>
          ) : null}

          {job !== null ? (
            <span className="chip" role="status">
              <Spinner size="sm" />
              {JOB_TYPE_LABEL[job.type]} · {JOB_STATUS_LABEL[job.status]}
              <progress
                className="home-progress"
                value={job.progress.done}
                max={Math.max(job.progress.total, 1)}
              />
              <span className="faint">
                {job.progress.done}/{job.progress.total}
              </span>
            </span>
          ) : null}

          {remap.isError ? (
            <span className="chip home-status__chip-error">{describeError(remap.error)}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function HomePage() {
  // ASSUMPTION A-5: write the browser timezone once, if the setting is still null.
  useTimezoneBootstrap();
  useDocumentTitle(pageTitle('Profiles'));

  const timezone = useAppTimezone();
  const mediaEnabled = useMediaEnabled();
  // Same query key as the strip's, so the badge and the strip come from one fetch and can never
  // disagree about how many are unread.
  const unreadTotal = useUnreadNotifications().data?.unreadTotal ?? 0;
  const profilesQuery = useProfilesQuery();
  const groupsQuery = useGroupsQuery();
  const statusQuery = useStatusQuery();
  const startScrape = useStartScrape();

  // Table filters are session state — deliberately not persisted, and both compose as ANDs with
  // the issues chip.
  const [archiveFilter, setArchiveFilter] = useState<ArchiveFilter>('active');
  const [groupFilter, setGroupFilter] = useState<GroupFilter>('all');
  const [issuesFilterOn, setIssuesFilterOn] = useState(false);
  const [settingsTab, setSettingsTab] = useState<'general' | 'scrapers' | 'watching' | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [scrapeNowOpen, setScrapeNowOpen] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [historyFor, setHistoryFor] = useState<ProfileRowDTO | null>(null);
  const [backfillFor, setBackfillFor] = useState<ProfileRowDTO | null>(null);

  const profiles = useMemo(() => profilesQuery.data?.profiles ?? [], [profilesQuery.data]);
  const groups = groupsQuery.data?.groups ?? [];

  const issueIds = useMemo(() => {
    const issues = statusQuery.data?.issues;
    if (issues === undefined) return new Set<number>();
    return new Set<number>([...issues.errorProfiles, ...issues.mapperErrorProfiles]);
  }, [statusQuery.data]);

  // Deleting the selected group would leave the <select> rendering blank and the table empty, so
  // the effective value falls back to "All" until the user picks again.
  const effectiveGroupFilter: GroupFilter =
    typeof groupFilter === 'number' && !groups.some((group) => group.id === groupFilter)
      ? 'all'
      : groupFilter;

  const queued = statusQuery.data?.queue.queued ?? 0;
  const running = statusQuery.data?.queue.running ?? 0;
  const busy = queued + running > 0;

  // PRD: "no profiles at all" is an onboarding screen centred on the Add action.
  const onboarding = profilesQuery.isSuccess && profiles.length === 0;

  return (
    <PostPreviewProvider mediaEnabled={mediaEnabled} timezone={timezone}>
    <div className="page home">
      <Link
        className="icon-btn home-bell"
        to={NOTIFICATIONS_PATH}
        aria-label={
          unreadTotal > 0 ? `Notifications (${unreadTotal} unread)` : 'Notifications'
        }
      >
        <BellIcon />
        {unreadTotal > 0 ? (
          <span className="home-bell__badge" aria-hidden="true">
            {unreadTotal > 99 ? '99+' : unreadTotal}
          </span>
        ) : null}
      </Link>
      <button
        type="button"
        className="icon-btn home-gear"
        aria-label="Settings"
        onClick={() => setSettingsTab('general')}
      >
        <GearIcon />
      </button>

      <div className="content">
        <h1 className="home-welcome">Welcome back!</h1>

        <HomeStatusStack
          status={statusQuery.data}
          issueCount={issueIds.size}
          issuesFilterOn={issuesFilterOn}
          onToggleIssuesFilter={() => setIssuesFilterOn((current) => !current)}
          onOpenSettings={(tab) => setSettingsTab(tab ?? 'general')}
          timezone={timezone}
        />

        {profilesQuery.isPending ? <Spinner size="lg" label="Loading profiles…" /> : null}

        {profilesQuery.isError ? (
          <ErrorState
            title="Couldn't load profiles"
            error={profilesQuery.error}
            onRetry={() => void profilesQuery.refetch()}
            retrying={profilesQuery.isFetching}
          />
        ) : null}

        {onboarding ? (
          <EmptyState
            title="Track your first profiles"
            description="Paste one or more LinkedIn profile URLs. Nothing is scraped until you say so — new profiles start on Manual."
            action={
              <button type="button" className="btn-primary" onClick={() => setAddOpen(true)}>
                <PlusIcon />
                Add profiles
              </button>
            }
          />
        ) : null}

        {profilesQuery.isSuccess && !onboarding ? (
          <>
            <div className="home-actions">
              {/* Feed leads the row (FEED_SPEC): monitoring is what the app is opened for. */}
              <Link className="btn-primary" to={FEED_PATH}>
                <FeedIcon />
                Feed
              </Link>
              {/* Saved sits beside Feed: reading and keeping are the two things this app is
                  opened for (SPEC `saved-posts`). */}
              <Link className="btn-primary" to={SAVED_PATH}>
                <BookmarkFilledIcon />
                Saved
              </Link>
              {/* Watching is the third reading surface (SPEC `New Post Watching` § Homescreen):
                  what just went up, while a comment is still early. */}
              <Link className="btn-primary" to={WATCH_PATH}>
                <EyeIcon />
                Watch
              </Link>
              {/* Content Performance closes the reading row (SPEC `Content Performance`
                  § Navigation): the one screen fed by the user's own analytics export rather
                  than by scraping. */}
              <Link className="btn-primary" to={CONTENT_PATH}>
                <ChartIcon />
                Content
              </Link>
              <button type="button" className="btn-primary" onClick={() => setAddOpen(true)}>
                <PlusIcon />
                Add
              </button>
              <button type="button" className="btn-ghost" onClick={() => setScrapeNowOpen(true)}>
                <RefreshIcon />
                Scrape
              </button>
              {busy ? (
                <span className="home-actions__queue" role="status">
                  <Spinner size="sm" />
                  {running} running · {queued} queued
                </span>
              ) : null}
            </div>

            {/* SPEC `watch-snooze`: day-to-day control of watching, directly under the buttons —
                status, today's figures and the snooze row. Hidden while watching is switched off. */}
            <WatchCallout />

            {/* SPEC `notifications`: between the callout and Groups, unread only. */}
            <NewViralStrip mediaEnabled={mediaEnabled} />

            <GroupsSection
              groups={groups}
              profiles={profiles}
              timezone={timezone}
              isPending={groupsQuery.isPending}
              error={groupsQuery.error}
              onRetry={() => void groupsQuery.refetch()}
              onScrapeGroup={(groupId) => startScrape.mutate({ groupIds: [groupId] })}
              createOpen={createGroupOpen}
              onOpenCreate={() => setCreateGroupOpen(true)}
              onCloseCreate={() => setCreateGroupOpen(false)}
            />

            <section className="section">
              <div className="section-head">
                <h2 className="section-title">Profiles</h2>
                <div className="section-head__actions section-head__actions--filters">
                  <label className="section-filter">
                    <span className="section-filter__label">Archive</span>
                    <select
                      className="select"
                      value={archiveFilter}
                      onChange={(event) =>
                        setArchiveFilter(event.target.value as ArchiveFilter)
                      }
                    >
                      <option value="all">All</option>
                      <option value="active">Active</option>
                      <option value="archived">Archived</option>
                    </select>
                  </label>
                  <label className="section-filter">
                    <span className="section-filter__label">Group</span>
                    <select
                      className="select"
                      value={
                        typeof effectiveGroupFilter === 'number'
                          ? String(effectiveGroupFilter)
                          : effectiveGroupFilter
                      }
                      onChange={(event) => {
                        const { value } = event.target;
                        setGroupFilter(
                          value === 'all' || value === 'none' ? value : Number(value),
                        );
                      }}
                    >
                      <option value="all">All</option>
                      {groups.map((group) => (
                        <option key={group.id} value={String(group.id)}>
                          {group.name}
                        </option>
                      ))}
                      <option value="none">No groups</option>
                    </select>
                  </label>
                </div>
              </div>
              <div className="card card--table">
                <ProfilesTable
                  profiles={profiles}
                  groups={groups}
                  timezone={timezone}
                  archiveFilter={archiveFilter}
                  groupFilter={effectiveGroupFilter}
                  issueIds={issueIds}
                  issuesFilterOn={issuesFilterOn}
                  onOpenHistory={setHistoryFor}
                  onScrapeProfile={(profileId) => startScrape.mutate({ profileIds: [profileId] })}
                  onBackfillProfile={setBackfillFor}
                  onNewGroup={() => setCreateGroupOpen(true)}
                />
              </div>
            </section>
          </>
        ) : null}
      </div>

      <AddProfilesDialog open={addOpen} onClose={() => setAddOpen(false)} />

      <ScrapeNowDialog
        open={scrapeNowOpen}
        profiles={profiles}
        groups={groups}
        onClose={() => setScrapeNowOpen(false)}
      />

      {historyFor !== null ? (
        <ScrapeHistoryDialog
          open
          profileId={historyFor.id}
          profileName={historyFor.name ?? historyFor.slug}
          timezone={timezone}
          onClose={() => setHistoryFor(null)}
        />
      ) : null}

      {backfillFor !== null ? (
        <BackfillDialog profile={backfillFor} onClose={() => setBackfillFor(null)} />
      ) : null}

      <SettingsModal
        open={settingsTab !== null}
        initialTab={settingsTab ?? 'general'}
        onClose={() => setSettingsTab(null)}
      />
    </div>
    </PostPreviewProvider>
  );
}

export default HomePage;
